import { create } from 'zustand';
import { CLOUD_API_URL } from '../config/serverConfig';
import { getHeaders } from '../lib/useQobuz';

export interface RecentAlbum {
  album_id: string;
  source: string | null;
  title: string;
  artist: string | null;
  artwork_url: string | null;
  quality_label: string | null;
  played_at: string;
}

interface RecentAlbumsState {
  albums: RecentAlbum[];
  loading: boolean;
  loaded: boolean;
  fetchRecent: (force?: boolean) => Promise<void>;
  saveRecent: (album: {
    album_id: string;
    source?: string | null;
    title: string;
    artist?: string | null;
    artwork_url?: string | null;
    quality_label?: string | null;
  }) => void;
  removeRecent: (albumId: string) => void;
}

const MAX_RECENT = 25;
const LS_KEY = 'maestro_recent_albums';

function loadFromLocalStorage(): RecentAlbum[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function saveToLocalStorage(albums: RecentAlbum[]) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(albums.slice(0, MAX_RECENT)));
  } catch { /* quota exceeded or private mode */ }
}

export const useRecentAlbumsStore = create<RecentAlbumsState>((set, get) => ({
  albums: loadFromLocalStorage(),
  loading: false,
  loaded: false,

  fetchRecent: async (force = false) => {
    const state = get();
    if (state.loaded && !force) return;
    if (state.loading) return;

    set({ loading: true });
    try {
      const resp = await fetch(`${CLOUD_API_URL}/api/recent-albums`, {
        headers: getHeaders(),
        credentials: 'omit',
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const serverAlbums: RecentAlbum[] = data.albums || [];

      const localAlbums = get().albums;
      const merged = mergeAlbumLists(serverAlbums, localAlbums);

      set({ albums: merged, loaded: true, loading: false });
      saveToLocalStorage(merged);
    } catch (err) {
      console.warn('[recentAlbumsStore] fetchRecent failed:', err);
      set({ loading: false, loaded: true });
    }
  },

  saveRecent: (album) => {
    const entry: RecentAlbum = {
      album_id: album.album_id,
      source: album.source ?? null,
      title: album.title,
      artist: album.artist ?? null,
      artwork_url: album.artwork_url ?? null,
      quality_label: album.quality_label ?? null,
      played_at: new Date().toISOString(),
    };

    set((prev) => {
      const filtered = prev.albums.filter((a) => !isSameAlbum(a, entry));
      const updated = [entry, ...filtered].slice(0, MAX_RECENT);
      saveToLocalStorage(updated);
      return { albums: updated, loaded: true };
    });

    fetch(`${CLOUD_API_URL}/api/recent-albums`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        album_id: album.album_id,
        source: album.source ?? null,
        title: album.title,
        artist: album.artist ?? null,
        artwork_url: album.artwork_url ?? null,
        quality_label: album.quality_label ?? null,
      }),
    }).catch((err) => {
      console.warn('[recentAlbumsStore] saveRecent API call failed:', err);
    });
  },

  removeRecent: (albumId) => {
    set((prev) => {
      const updated = prev.albums.filter((a) => a.album_id !== albumId);
      saveToLocalStorage(updated);
      return { albums: updated };
    });

    fetch(`${CLOUD_API_URL}/api/recent-albums/${encodeURIComponent(albumId)}`, {
      method: 'DELETE',
      headers: getHeaders(),
      credentials: 'omit',
    }).catch((err) => {
      console.warn('[recentAlbumsStore] removeRecent API call failed:', err);
    });
  },
}));

function normalizeKey(title: string | null, artist: string | null): string {
  return `${(title || '').trim().toLowerCase()}::${(artist || '').trim().toLowerCase()}`;
}

function isSameAlbum(a: RecentAlbum, b: RecentAlbum): boolean {
  if (a.album_id === b.album_id) return true;
  if (!a.title || !b.title) return false;
  return normalizeKey(a.title, a.artist) === normalizeKey(b.title, b.artist);
}

function mergeAlbumLists(server: RecentAlbum[], local: RecentAlbum[]): RecentAlbum[] {
  const result: RecentAlbum[] = [];
  const seenIds = new Set<string>();
  const seenKeys = new Set<string>();

  const addIfNew = (a: RecentAlbum) => {
    const key = normalizeKey(a.title, a.artist);
    if (seenIds.has(a.album_id) || (a.title && seenKeys.has(key))) return;
    seenIds.add(a.album_id);
    if (a.title) seenKeys.add(key);
    result.push(a);
  };

  const allSorted = [...server, ...local]
    .sort((a, b) => (b.played_at || '').localeCompare(a.played_at || ''));

  for (const a of allSorted) {
    addIfNew(a);
  }

  return result.slice(0, MAX_RECENT);
}
